import React, { useState } from 'react';
import { useGame } from '../state/gameStore.tsx';
import { NPC_FLEETS } from '@galaxy/shared';
import type { CombatReport } from '@galaxy/shared';
import { MetalIcon, He3Icon, CreditsIcon, CrosshairIcon } from '../assets/icons.tsx';

export const CombatView: React.FC = () => {
  const { state, attackNpc, combatModalReport, setCombatModalReport } = useGame();
  const [isAttacking, setIsAttacking] = useState<string | null>(null);
  const [activeRoundTab, setActiveRoundTab] = useState<number>(1);

  if (!state) return null;

  const { recent_combats, fleet } = state;
  const hasFleetShips = fleet.slots.some((s) => s.ship_count > 0);

  const handleAttack = async (npcId: string) => {
    try {
      setIsAttacking(npcId);
      await attackNpc(npcId);
    } catch {
      // Handled in store
    } finally {
      setIsAttacking(null);
    }
  };

  return (
    <div style={{ padding: '12px', display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* Title */}
      <div>
        <h2 style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-bright)', letterSpacing: 0.5 }}>
          TRUNG TÂM TÁC CHIẾN & CHIẾN BÁO
        </h2>
        <p style={{ fontSize: 12, color: 'var(--text-dim)' }}>
          Khiêu chiến các hạm đội hải tặc hư không để thu thập tài nguyên và kinh nghiệm
        </p>
      </div>

      {!hasFleetShips && (
        <div className="sci-card" style={{ borderColor: 'var(--status-red)', background: 'rgba(255, 23, 68, 0.08)' }}>
          <div style={{ color: 'var(--status-red)', fontSize: 13, fontWeight: 700, marginBottom: 4 }}>
            ⚠️ Chưa có chiến hạm trong Đội hình 3×3!
          </div>
          <div style={{ color: 'var(--text-dim)', fontSize: 11 }}>
            Hãy vào tab <strong>ĐỘI HÌNH</strong> để triển khai tàu chiến trước khi xuất kích giao tranh.
          </div>
        </div>
      )}

      {/* NPC Targets */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {Object.values(NPC_FLEETS).map((npc) => {
          const totalNpcShips = npc.slots.reduce((a, s) => a + s.ship_count, 0);
          return (
            <div key={npc.id} className="sci-card" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <CrosshairIcon size={20} color="var(--status-red)" />
                  <div>
                    <h3 style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-bright)' }}>
                      {npc.name}
                    </h3>
                    <span style={{ fontSize: 11, color: 'var(--text-dim)' }}>
                      Quy mô: <span className="mono" style={{ color: 'var(--status-red)', fontWeight: 700 }}>{totalNpcShips} tàu</span>
                    </span>
                  </div>
                </div>

                <span className="mono" style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: 'var(--status-red)',
                  background: 'rgba(255, 23, 68, 0.1)',
                  border: '1px solid rgba(255, 23, 68, 0.3)',
                  padding: '2px 8px',
                  borderRadius: 3,
                }}>
                  CẤP {npc.level}
                </span>
              </div>

              <p style={{ fontSize: 11, color: 'var(--text-dim)', lineHeight: 1.3 }}>
                {npc.description}
              </p>

              {/* Bounty Rewards & Attack Button */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTop: '1px solid var(--border-panel)', paddingTop: 6 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11 }}>
                  <span style={{ color: 'var(--text-dim)' }}>Chiến lợi phẩm:</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <MetalIcon size={12} />
                    <span className="mono" style={{ color: 'var(--status-green)' }}>+{npc.reward.metal}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <He3Icon size={12} />
                    <span className="mono" style={{ color: 'var(--status-green)' }}>+{npc.reward.he3}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <CreditsIcon size={12} />
                    <span className="mono" style={{ color: 'var(--status-green)' }}>+{npc.reward.credits}</span>
                  </div>
                </div>

                <button
                  className="sci-btn sci-btn-red"
                  disabled={!hasFleetShips || isAttacking === npc.id}
                  onClick={() => handleAttack(npc.id)}
                  style={{ padding: '6px 14px', fontSize: 12 }}
                >
                  {isAttacking === npc.id ? 'Đang tác chiến...' : 'TẤN CÔNG'}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Recent Combat Reports */}
      {recent_combats.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-bright)', marginBottom: 8 }}>
            NHẬT KÝ CHIẾN BÁO GẦN ĐÂY
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {recent_combats.map((report) => (
              <div
                key={report.id}
                onClick={() => setCombatModalReport(report)}
                style={{
                  background: 'var(--bg-panel)',
                  border: '1px solid var(--border-panel)',
                  borderRadius: 4,
                  padding: '8px 12px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  cursor: 'pointer',
                }}
              >
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    {report.target_type === 'pvp_defense' ? (
                      <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 2, background: 'rgba(255, 179, 0, 0.2)', color: 'var(--accent-yellow)', border: '1px solid rgba(255, 179, 0, 0.4)', fontWeight: 700 }}>
                        THỦ THÀNH PvP
                      </span>
                    ) : report.target_type === 'pvp_attack' ? (
                      <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 2, background: 'rgba(255, 23, 68, 0.2)', color: 'var(--status-red)', border: '1px solid rgba(255, 23, 68, 0.4)', fontWeight: 700 }}>
                        ĐỘT KÍCH PvP
                      </span>
                    ) : (
                      <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 2, background: 'rgba(0, 229, 255, 0.2)', color: 'var(--accent-cyan)', border: '1px solid rgba(0, 229, 255, 0.4)', fontWeight: 700 }}>
                        SĂN HẢI TẶC
                      </span>
                    )}
                    <span style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: report.result === 'victory' ? 'var(--status-green)' : 'var(--status-red)',
                    }}>
                      {report.result === 'victory' ? '🏆 CHIẾN THẮNG' : '💀 THẤT BẠI'}
                    </span>
                    <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-bright)' }}>
                      vs {report.target_name}
                    </span>
                  </div>
                  <div className="mono" style={{ fontSize: 10, color: 'var(--text-dim)', marginTop: 2 }}>
                    {new Date(report.created_at).toLocaleTimeString()} | {report.rounds_count} Hiệp đấu
                  </div>
                </div>

                <button className="sci-btn" style={{ padding: '4px 8px', fontSize: 10 }}>
                  XEM CHI TIẾT
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Battle Report Replay Modal */}
      {combatModalReport && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.85)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: 16,
        }}>
          <div className="sci-card sci-card-glow" style={{ width: '100%', maxWidth: 500, maxHeight: '90vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 12 }}>
            {/* Header banner */}
            <div style={{
              background: combatModalReport.result === 'victory' ? 'rgba(0, 230, 118, 0.15)' : 'rgba(255, 23, 68, 0.15)',
              border: `1px solid ${combatModalReport.result === 'victory' ? 'var(--status-green)' : 'var(--status-red)'}`,
              padding: '12px',
              borderRadius: 6,
              textAlign: 'center',
            }}>
              <h2 style={{
                fontSize: 20,
                fontWeight: 700,
                color: combatModalReport.result === 'victory' ? 'var(--status-green)' : 'var(--status-red)',
                marginBottom: 2,
              }}>
                {combatModalReport.result === 'victory' ? '🏆 CHIẾN THẮNG VANG DỘI' : '💀 HẠM ĐỘI BỊ ĐÁNH CHÌM'}
              </h2>
              <div style={{ fontSize: 12, color: 'var(--text-bright)' }}>
                Tác chiến đối đầu với: {combatModalReport.target_name}
              </div>
            </div>

            {/* Bounty Reward if victory */}
            {combatModalReport.result === 'victory' && (
              <div className="sci-card" style={{ padding: '8px 12px' }}>
                <span style={{ fontSize: 11, color: 'var(--text-dim)', fontWeight: 700, display: 'block', marginBottom: 4 }}>
                  CHIẾN LỢI PHẨM THU ĐƯỢC:
                </span>
                <div style={{ display: 'flex', gap: 14 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <MetalIcon size={14} />
                    <span className="mono" style={{ color: 'var(--status-green)', fontWeight: 700 }}>
                      +{combatModalReport.loot.metal}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <He3Icon size={14} />
                    <span className="mono" style={{ color: 'var(--status-green)', fontWeight: 700 }}>
                      +{combatModalReport.loot.he3}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <CreditsIcon size={14} />
                    <span className="mono" style={{ color: 'var(--status-green)', fontWeight: 700 }}>
                      +{combatModalReport.loot.credits}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span style={{ color: 'var(--accent-amber)', fontSize: 11 }}>EXP</span>
                    <span className="mono" style={{ color: 'var(--accent-amber)', fontWeight: 700 }}>
                      +{combatModalReport.loot.exp}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Losses Summary */}
            <div className="sci-card" style={{ padding: '8px 12px' }}>
              <span style={{ fontSize: 11, color: 'var(--text-dim)', fontWeight: 700, display: 'block', marginBottom: 4 }}>
                TỔN THẤT HAI BÊN:
              </span>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 12 }}>
                <div>
                  <div style={{ color: 'var(--accent-cyan)', fontWeight: 700 }}>Phe Ta:</div>
                  {combatModalReport.losses.attacker_losses.map((l) => (
                    <div key={l.slot_index} className="mono" style={{ color: l.lost_count > 0 ? 'var(--status-red)' : 'var(--text-dim)' }}>
                      Ô #{l.slot_index}: Mất {l.lost_count} (Còn {l.remaining_count})
                    </div>
                  ))}
                </div>
                <div>
                  <div style={{ color: 'var(--status-red)', fontWeight: 700 }}>Phe Địch:</div>
                  {combatModalReport.losses.defender_losses.map((l) => (
                    <div key={l.slot_index} className="mono" style={{ color: l.lost_count > 0 ? 'var(--status-green)' : 'var(--text-dim)' }}>
                      Ô #{l.slot_index}: Mất {l.lost_count} (Còn {l.remaining_count})
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Tactical Round Log */}
            <div>
              <span style={{ fontSize: 12, color: 'var(--text-dim)', fontWeight: 700, display: 'block', marginBottom: 6 }}>
                DIỄN BIẾN LƯỢT ĐẤU (TỔNG CỘNG {combatModalReport.rounds_count} HIỆP):
              </span>

              {/* Round selector tabs */}
              <div style={{ display: 'flex', gap: 4, overflowX: 'auto', marginBottom: 8, paddingBottom: 2 }}>
                {combatModalReport.rounds.map((r) => (
                  <button
                    key={r.round}
                    onClick={() => setActiveRoundTab(r.round)}
                    style={{
                      background: activeRoundTab === r.round ? 'var(--accent-cyan)' : 'var(--bg-panel-subtle)',
                      color: activeRoundTab === r.round ? 'var(--bg-space)' : 'var(--text-bright)',
                      border: '1px solid var(--border-panel)',
                      borderRadius: 3,
                      padding: '4px 8px',
                      fontSize: 11,
                      fontWeight: 700,
                      cursor: 'pointer',
                      flexShrink: 0,
                    }}
                  >
                    Hiệp {r.round}
                  </button>
                ))}
              </div>

              {/* Actions in active round */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 180, overflowY: 'auto' }}>
                {combatModalReport.rounds
                  .find((r) => r.round === activeRoundTab)
                  ?.actions.map((act, actIdx) => {
                    const isQuantumLaser = state.player.equipped_combat_vfx === 'vfx_combat_quantum_laser' || state.player.equipped_cosmetics?.combat_vfx === 'vfx_combat_quantum_laser';
                    const isAttackerWithLaser = act.side === 'attacker' && isQuantumLaser;
                    return (
                      <div
                        key={actIdx}
                        style={{
                          background: isAttackerWithLaser ? 'rgba(6, 182, 212, 0.08)' : 'var(--bg-panel-subtle)',
                          borderLeft: `3px solid ${act.side === 'attacker' ? (isQuantumLaser ? '#06b6d4' : 'var(--accent-cyan)') : 'var(--status-red)'}`,
                          boxShadow: isAttackerWithLaser ? '0 0 8px rgba(6, 182, 212, 0.2)' : undefined,
                          padding: '6px 8px',
                          fontSize: 11,
                          borderRadius: 3,
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                          <span style={{ fontWeight: 700, color: act.side === 'attacker' ? (isQuantumLaser ? '#22d3ee' : 'var(--accent-cyan)') : 'var(--status-red)' }}>
                            {act.side === 'attacker' ? (isQuantumLaser ? '⚡ Phe Ta [Laser Lượng Tử]' : 'Phe Ta') : 'Phe Địch'} (Ô #{act.attacker_slot}) ➔ Nhắm Ô #{act.target_slot}
                          </span>
                          <span className="mono" style={{ color: act.is_hit ? 'var(--status-green)' : 'var(--text-dim)' }}>
                            {act.is_hit ? '🎯 TRÚNG ĐÍCH' : '💨 BẮN TRƯỢT'}
                          </span>
                        </div>

                      {act.is_hit && (
                        <div className="mono" style={{ color: 'var(--text-dim)', fontSize: 10 }}>
                          Sát thương: <span style={{ color: 'var(--text-bright)' }}>{act.damage_dealt}</span>
                          {act.shield_absorbed > 0 && ` (Khiên chặn: ${act.shield_absorbed})`}
                          {act.armor_mitigated > 0 && ` (Giáp giảm: ${act.armor_mitigated})`}
                          {act.ships_destroyed > 0 && (
                            <span style={{ color: 'var(--status-red)', fontWeight: 700 }}>
                              {' '}💥 Bị phá hủy: {act.ships_destroyed} tàu!
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Close Button */}
            <button
              className="sci-btn"
              onClick={() => setCombatModalReport(null)}
              style={{ marginTop: 6, padding: '10px' }}
            >
              ĐÓNG CHIẾN BÁO
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
